import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useState } from "react";
import { UserPlus, Users, BarChart3, LogOut, Menu, X, Shield, Eye, FileBarChart2, ScanLine, Calendar, Star, Handshake, Send } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { customerUrl } from "@/lib/public-urls";
import { NotificationBell } from "@/components/NotificationBell";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const baseNav = [
  { to: "/af-portal/dashboard", label: "Översikt", icon: BarChart3, adminOnly: false },
  { to: "/af-portal/kunder", label: "Kundregister", icon: Users, adminOnly: false },
  { to: "/af-portal/event", label: "Event", icon: Calendar, adminOnly: false },
  { to: "/af-portal/formaner", label: "Förmåner", icon: Star, adminOnly: true },
  { to: "/af-portal/partners", label: "Partners", icon: Handshake, adminOnly: true },
  { to: "/af-portal/utskick", label: "Kundutskick", icon: Send, adminOnly: false },
  { to: "/af-portal/rapporter", label: "Rapporter & revision", icon: FileBarChart2, adminOnly: true },
  { to: "/af-portal/personal", label: "Personal & behörigheter", icon: Shield, adminOnly: true },
] as const;

export function StaffLayout({ children, title }: { children: React.ReactNode; title?: string; store?: string; staffName?: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [enteringTest, setEnteringTest] = useState(false);
  const { session, staff, loading, signOut } = useAuth();

  if (loading) {
    return <div className="min-h-dvh flex items-center justify-center bg-[var(--color-soft)] text-muted-foreground">Laddar…</div>;
  }
  if (!session) return <Navigate to="/af-portal" replace />;
  if (!staff || !staff.active) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-[var(--color-ink)] text-white px-6">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-display mb-2">Åtkomst nekad</h1>
          <p className="text-white/70 text-sm mb-6">Det här kontot är inte registrerat som aktiv butikspersonal.</p>
          <button onClick={async () => { await signOut(); navigate("/af-portal"); }} className="btn-pill">Logga ut</button>
        </div>
      </div>
    );
  }
  const adminOnlyPaths = ["/af-portal/formaner", "/af-portal/personal", "/af-portal/rapporter", "/af-portal/partners"];
  if (adminOnlyPaths.includes(location.pathname) && staff.role !== "admin") {
    return <Navigate to="/af-portal/dashboard" replace />;
  }

  const nav = baseNav.filter(n => !n.adminOnly || staff.role === "admin");
  const staffName = staff.full_name || staff.email;
  const store = staff.store_id ? "" : "";

  async function handleLogout() {
    await signOut();
    navigate("/af-portal");
  }

  async function enterTestMode() {
    if (enteringTest) return;
    setEnteringTest(true);
    try {
      const { data, error } = await supabase.functions.invoke("enter-test-mode", { body: {} });
      if (error || !data?.access_token || !data?.refresh_token) {
        throw new Error(error?.message || data?.error || "Kunde inte starta testläge");
      }
      const { error: sessErr } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      if (sessErr) throw sessErr;
      toast.success("Testläge aktivt — du visar kundportalen som Test Testsson");
      navigate("/access/dashboard");
    } catch (e) {
      toast.error((e as Error).message);
      setEnteringTest(false);
    }
  }

  const NavItems = ({ onItemClick }: { onItemClick?: () => void }) => (
    <>
      {nav.map((n) => {
        const Icon = n.icon;
        const active = location.pathname === n.to;
        return (
          <Link
            key={n.to}
            to={n.to}
            onClick={onItemClick}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition font-display ${
              active
                ? "bg-white/10 text-[var(--color-primary)] font-semibold"
                : "text-white/75 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Icon size={17} />
            {n.label}
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="staff-layout min-h-screen flex bg-[var(--color-soft)]">
      <aside className="staff-sidebar hidden lg:flex w-[260px] shrink-0 bg-[var(--color-ink)] text-white flex-col self-start sticky top-4 rounded-2xl shadow-lg overflow-hidden ml-4 my-4">
        <div className="px-7 pt-8 pb-6 border-b border-white/10">
          <div className="font-display font-light text-white tracking-wider text-[18px] leading-tight">LAGAMOBILEN</div>
          <div className="font-display font-semibold text-[var(--color-gold)] text-[26px] tracking-[0.2em] leading-tight">ACCESS</div>
          <div className="mt-2 inline-block text-[10px] tracking-[0.2em] text-white/50 uppercase">ÅF-Portal</div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          <NavItems />
        </nav>

        <div className="px-7 py-5 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Inloggad</div>
          <div className="font-display font-semibold text-sm text-white mt-1 truncate">{staffName}</div>
          <div className="text-xs text-white/50">{staff.role === "admin" ? "Admin" : "Personal"}{store && ` · ${store}`}</div>
          <a
            href={customerUrl("/access/skapa-konto?assisted=1")}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition text-xs font-display tracking-wide"
          >
            <UserPlus size={13} /> Hjälp kund att registrera
          </a>
          <button
            type="button"
            onClick={enterTestMode}
            disabled={enteringTest}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full bg-[var(--color-gold)]/15 border border-[var(--color-gold)]/30 text-[var(--color-gold)] hover:bg-[var(--color-gold)]/25 transition text-xs font-display tracking-wide disabled:opacity-60"
            title="Logga in som Test Testsson för att testa kundflödet"
          >
            <Eye size={13} /> {enteringTest ? "Startar testläge…" : "Visa kundvy (testläge)"}
          </button>
          <button
            onClick={handleLogout}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full border border-white/15 text-white/80 hover:bg-white/5 transition text-xs font-display tracking-wide"
          >
            <LogOut size={13} /> Logga ut
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <aside className="staff-sidebar relative ml-auto w-[280px] h-full bg-[var(--color-ink)] text-white flex flex-col overflow-y-auto">
            <div className="px-7 pt-6 pb-4 flex items-start justify-between border-b border-white/10">
              <div>
                <div className="font-display font-light tracking-wider text-[16px] leading-tight">LAGAMOBILEN</div>
                <div className="font-display font-semibold text-[var(--color-gold)] text-2xl tracking-[0.2em] leading-tight">ACCESS</div>
                <div className="mt-1 text-[10px] tracking-[0.2em] text-white/50 uppercase">ÅF-Portal</div>
              </div>
              <button onClick={() => setMobileOpen(false)} aria-label="Stäng meny" className="p-2 rounded-lg hover:bg-white/10 transition">
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1">
              <NavItems onItemClick={() => setMobileOpen(false)} />
            </nav>
            <div className="px-7 py-5 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Inloggad</div>
              <div className="font-display font-semibold text-sm text-white mt-1 truncate">{staffName}</div>
              <div className="text-xs text-white/50">{staff.role === "admin" ? "Admin" : "Personal"}</div>
              <button
                onClick={() => { setMobileOpen(false); handleLogout(); }}
                className="mt-4 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-full border border-white/15 text-white/80 hover:bg-white/5 transition text-xs font-display tracking-wide"
              >
                <LogOut size={13} /> Logga ut
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="staff-sidebar bg-[var(--color-ink)] text-white shrink-0 lg:hidden sticky top-0 z-30 border-b border-white/10">
          <div className="flex items-center justify-between px-5 py-3">
            <Link to="/af-portal/dashboard" className="flex items-baseline gap-2">
              <span className="font-display font-light tracking-[0.2em] text-[10px] uppercase">Lagamobilen</span>
              <span className="font-display font-semibold text-[var(--color-gold)] text-[18px] tracking-[0.2em]">ACCESS</span>
              <span className="text-[9px] tracking-[0.2em] text-white/50 uppercase">ÅF</span>
            </Link>
            <div className="flex items-center gap-1">
              <Link
                to="/af-portal/losa-in"
                aria-label="Lös in förmånskod"
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-[var(--color-gold)] text-[var(--color-ink)] hover:opacity-90 transition text-xs font-display font-semibold"
              >
                <ScanLine size={14} /> Lös in
              </Link>
              <NotificationBell variant="dark" />
              <button onClick={() => setMobileOpen(true)} aria-label="Öppna meny" className="p-2 rounded-lg hover:bg-white/10 transition">
                <Menu size={20} />
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 min-w-0 px-5 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10">
          <div className="hidden lg:flex items-center justify-end gap-2 mb-2">
            <Link
              to="/af-portal/losa-in"
              className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-[var(--color-gold)] text-[var(--color-ink)] hover:opacity-90 transition text-sm font-display font-semibold shadow-sm"
            >
              <ScanLine size={15} /> Lös in förmånskod
            </Link>
            <NotificationBell />
          </div>
          {title && <h1 className="!text-2xl sm:!text-3xl mb-6 text-foreground">{title}</h1>}
          {children}
        </main>
      </div>
    </div>
  );
}
